// src/pages/Admin/Adherents/hooks/useAdherentsAdmin.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getAllAdherentsWithGroupAndUsers,
} from "./../../../services/adherentServices";
import { getSettings } from "./../../../services/settingsServices";
import { getGroupsList } from "./../../../services/groupServices";
import { getDojos } from "./../../../services/dojoServices";
import { toast } from "react-toastify";
import { CHUNK, neighborsFromSeason } from "./helpers";

export default function useAdherentsAdmin() {
  // Base
  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [communeFilter, setCommuneFilter] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsAll, setGroupsAll] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");
  const [showGroupFilter, setShowGroupFilter] = useState(false);

  // Saison active (ex: "2024-2025") — exposée aux composants
  const [activeSeason, setActiveSeason] = useState("");

  // UI
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [sortOrder, setSortOrder] = useState("none");
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(CHUNK);
  const scrollRef = useRef(null);

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch((searchTerm || "").trim().toLowerCase()),
      250
    );
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Chargement initial
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [adherentsData, settingsData, groupsData, dojoList] =
          await Promise.all([
            getAllAdherentsWithGroupAndUsers(),
            getSettings(),
            getGroupsList(),
            getDojos(),
          ]);

        const rows = Array.isArray(adherentsData) ? adherentsData : [];
        setAdherents(rows);
        checkForDuplicates(rows);

        const allGroups = Array.isArray(groupsData) ? groupsData : [];
        setGroupsAll(allGroups);

        const dojoArr = Array.isArray(dojoList) ? dojoList : [];
        setDojos(dojoArr);

        if (dojoArr.length === 1) {
          const onlyId = dojoArr[0].id;
          setSelectedDojo(String(onlyId));
          setShowGroupFilter(true);
          setGroups(allGroups.filter((g) => g.dojo_id === onlyId));
        } else {
          setGroups(allGroups);
          setShowGroupFilter(false);
        }

        const fetchedSettings = settingsData?.[0];
        const active = fetchedSettings?.saison_active || "";
        setActiveSeason(active);
        const seasonsArr = neighborsFromSeason(active);
        setSeasons(seasonsArr);
        setSeasonFilter(active || "");
      } catch (e) {
        console.error("Erreur chargement liste adhérents :", e);
        toast.error("Erreur lors du chargement des adhérents");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Communes dérivées
  const communes = useMemo(
    () =>
      [...new Set(adherents.map((a) => a.commune).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "fr")
      ),
    [adherents]
  );

  // Liste filtrée
  const filtered = useMemo(() => {
    const search = debouncedSearch;
    return adherents
      .filter((a) => {
        const matchSearch =
          !search ||
          `${a.nom || ""} ${a.prenom || ""}`.toLowerCase().includes(search);
        const matchSeason = !seasonFilter || a.saison_active === seasonFilter;
        const matchGroup =
          !groupFilter || (a?.Groups || []).some((g) => g?.nom === groupFilter);
        const matchStatus =
          !statusFilter || a.statut_inscription === statusFilter;
        const matchCommune = !communeFilter || a.commune === communeFilter;
        return matchSearch && matchSeason && matchGroup && matchStatus && matchCommune;
      })
      .sort((a, b) => {
        if (sortOrder === "none") return 0;
        const da = new Date(a.date_naissance || 0).getTime();
        const db = new Date(b.date_naissance || 0).getTime();
        return sortOrder === "asc" ? da - db : db - da;
      });
  }, [
    adherents,
    debouncedSearch,
    seasonFilter,
    groupFilter,
    statusFilter,
    communeFilter,
    sortOrder,
  ]);

  // Reset scroll/visible si filtres changent
  useEffect(() => {
    setVisibleCount(CHUNK);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [debouncedSearch, seasonFilter, groupFilter, statusFilter, communeFilter]);

  // Duplicates
  const checkForDuplicates = (rows) => {
    const names = rows.map(
      (a) => `${(a.nom || "").toLowerCase()} ${(a.prenom || "").toLowerCase()}`
    );
    const seen = new Set();
    const dups = [];
    for (const n of names) {
      if (seen.has(n)) dups.push(n);
      else seen.add(n);
    }
    setDuplicateWarning(
      dups.length
        ? `Attention, doublon détecté : ${[...new Set(dups)].join(", ")}`
        : null
    );
  };

  // Handlers
  const handleDojoChange = async (e) => {
    const dojoId = e.target.value;
    setSelectedDojo(dojoId);
    try {
      const all = await getGroupsList();
      const dojoGroups =
        dojoId === "" ? all : all.filter((g) => g.dojo_id === parseInt(dojoId, 10));
      setGroups(dojoGroups);
      setGroupFilter("");
      setShowGroupFilter(!!dojoId);
    } catch (err) {
      console.error(err);
      toast.error("Erreur chargement des groupes du dojo");
    }
  };

  const toggleSortOrder = () =>
    setSortOrder((o) => (o === "none" ? "asc" : o === "asc" ? "desc" : "none"));

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      setVisibleCount((v) =>
        v >= filtered.length ? v : Math.min(v + CHUNK, filtered.length)
      );
    }
  };

  // Utils publics
  const refreshAll = useCallback(async () => {
    try {
      const data = await getAllAdherentsWithGroupAndUsers();
      const rows = Array.isArray(data) ? data : [];
      setAdherents(rows);
      checkForDuplicates(rows);
    } catch (e) {
      console.error("Erreur lors du rechargement des adhérents:", e);
      toast.error("Erreur lors du chargement des adhérents");
    }
  }, []);

  const state = {
    adherents,
    loading,
    seasons,
    groups,
    groupsAll,
    dojos,
    selectedDojo,
    showGroupFilter,
    duplicateWarning,
    sortOrder,
    editingStatusId,
    visibleCount,
    scrollRef,

    searchTerm,
    seasonFilter,
    groupFilter,
    statusFilter,
    communeFilter,

    communes,
    filtered,

    activeSeason, // <-- exposé aux vues (DOB highlighting)
  };

  const setters = {
    setSearchTerm,
    setSeasonFilter,
    setGroupFilter,
    setStatusFilter,
    setCommuneFilter,
    setEditingStatusId,
  };

  const handlers = {
    handleDojoChange,
    toggleSortOrder,
    onScroll,
    refreshAll,
    setVisibleCount, // au besoin
  };

  return { state, setters, handlers };
}
